/*
 * frost-faq.js (owner: CONTENT) – <fb-accordion> progressive enhancement for <details>.
 * Without JS the native <details> toggle works. With JS:
 *   full  -> height 280 ms (ease-out) + answer fades and slides 6 px
 *   soft  -> height + fade only (no translate)
 *   none  -> instant toggle
 * Interruptible (reverses from the current height), optional "one open at a time"
 * (data-single), theme editor: block:select opens the selected question.
 * One-open mode keeps the tapped question in place while an answer ABOVE it collapses
 * (scroll compensation per frame), so it never slides away under the finger.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('fb-accordion')) return;

  var EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
  var EASE_CLOSE = 'cubic-bezier(0.4, 0, 0.2, 1)';

  function motion() {
    if (window.FROSTBEULE && typeof window.FROSTBEULE.motion === 'function') return window.FROSTBEULE.motion();
    return document.documentElement.getAttribute('data-motion') || 'full';
  }

  function canAnimate(el) {
    return typeof el.animate === 'function';
  }

  function scrollByInstant(dy) {
    try {
      window.scrollBy({ top: dy, left: 0, behavior: 'instant' });
    } catch (e) {
      window.scrollBy(0, dy);
    }
  }

  /*
   * Keeps `el` at its current viewport position while content above it changes height.
   * pin.hold(isBusy) corrects the drift at once (instant mode) and then once per frame until
   * isBusy() is false (max 900 ms). Only the residual is corrected, so it composes with native
   * scroll anchoring; any new user input (wheel, touch, key, pointer) releases the pin.
   */
  function createPin(el) {
    var top0 = el.getBoundingClientRect().top;
    var released = false;
    var events = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
    var opts = { passive: true, capture: true };

    function release() {
      if (released) return;
      released = true;
      events.forEach(function (type) {
        window.removeEventListener(type, release, opts);
      });
    }

    function fix() {
      if (!el.isConnected) return;
      var dy = el.getBoundingClientRect().top - top0;
      if (Math.abs(dy) >= 0.5) scrollByInstant(dy);
    }

    return {
      release: release,
      hold: function (isBusy) {
        fix();
        if (!isBusy()) {
          release();
          return;
        }
        events.forEach(function (type) {
          window.addEventListener(type, release, opts);
        });
        var start = performance.now();
        var tick = function (now) {
          if (released) return;
          fix();
          if (isBusy() && now - start < 900) {
            window.requestAnimationFrame(tick);
          } else {
            release();
          }
        };
        window.requestAnimationFrame(tick);
      },
    };
  }

  window.customElements.define(
    'fb-accordion',
    class extends HTMLElement {
      connectedCallback() {
        this.single = this.hasAttribute('data-single');
        /* JS manages exclusivity itself (native name-groups would close siblings without animation) */
        if (this.single) {
          this.items().forEach(function (d) {
            d.removeAttribute('name');
          });
        }
        this._onClick = this.onClick.bind(this);
        this._onBlockSelect = this.onBlockSelect.bind(this);
        this.addEventListener('click', this._onClick);
        document.addEventListener('shopify:block:select', this._onBlockSelect);
        this.openFromHash();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        document.removeEventListener('shopify:block:select', this._onBlockSelect);
        if (this._pin) this._pin.release();
        var self = this;
        this.items().forEach(function (d) {
          self.stop(d);
        });
      }

      items() {
        var self = this;
        return Array.prototype.filter.call(this.querySelectorAll('details'), function (d) {
          return d.closest('fb-accordion') === self;
        });
      }

      parts(details) {
        var panel = details.querySelector('.fb-faq__a');
        return { panel: panel, inner: panel ? panel.firstElementChild : null };
      }

      onClick(event) {
        var summary = event.target.closest('summary');
        if (!summary) return;
        var details = summary.parentElement;
        if (!details || details.tagName !== 'DETAILS' || details.closest('fb-accordion') !== this) return;

        event.preventDefault();
        var opening = !details.open || details.classList.contains('is-closing');
        if (opening) {
          if (this.single) {
            if (this._pin) this._pin.release();
            var pin = createPin(summary);
            var above = this.closeOthers(details);
            this.expand(details);
            if (above.length) {
              this._pin = pin;
              pin.hold(function () {
                return above.some(function (d) {
                  return d.classList.contains('is-closing');
                });
              });
            } else {
              pin.release();
            }
          } else {
            this.expand(details);
          }
        } else {
          this.collapse(details);
        }
      }

      /* Collapses every other open item; returns the ones above `current` (they shift it up). */
      closeOthers(current) {
        var self = this;
        var above = [];
        this.items().forEach(function (d) {
          if (d !== current && d.open && !d.classList.contains('is-closing')) {
            if (d.compareDocumentPosition(current) & Node.DOCUMENT_POSITION_FOLLOWING) above.push(d);
            self.collapse(d);
          }
        });
        return above;
      }

      /* Stops running animations and returns the panel height at this moment. */
      stop(details) {
        var p = this.parts(details);
        var height = p.panel ? p.panel.getBoundingClientRect().height : 0;
        if (details._fbAnim) {
          details._fbAnim.onfinish = null;
          details._fbAnim.cancel();
          details._fbAnim = null;
        }
        if (details._fbInnerAnim) {
          details._fbInnerAnim.cancel();
          details._fbInnerAnim = null;
        }
        return height;
      }

      expand(details) {
        var p = this.parts(details);
        var mode = motion();
        var wasOpen = details.open && details.classList.contains('is-closing');
        var from = wasOpen ? this.stop(details) : (this.stop(details), 0);

        details.classList.remove('is-closing');
        details.open = true;

        if (mode === 'none' || !p.panel || !canAnimate(p.panel)) return;

        var to = p.panel.scrollHeight;
        if (Math.abs(to - from) < 1) return;
        var duration = 280;

        details._fbAnim = p.panel.animate([{ height: from + 'px' }, { height: to + 'px' }], {
          duration: duration,
          easing: EASE_OUT,
        });
        details._fbAnim.onfinish = function () {
          details._fbAnim = null;
        };

        if (p.inner && !wasOpen) {
          var frames =
            mode === 'full'
              ? [
                  { opacity: 0, transform: 'translateY(-6px)' },
                  { opacity: 1, transform: 'none' },
                ]
              : [{ opacity: 0 }, { opacity: 1 }];
          details._fbInnerAnim = p.inner.animate(frames, {
            duration: duration,
            delay: 40,
            easing: EASE_OUT,
            fill: 'backwards',
          });
          details._fbInnerAnim.onfinish = function () {
            details._fbInnerAnim = null;
          };
        }
      }

      collapse(details) {
        var p = this.parts(details);
        var mode = motion();

        if (mode === 'none' || !p.panel || !canAnimate(p.panel)) {
          this.stop(details);
          details.classList.remove('is-closing');
          details.open = false;
          return;
        }

        var from = this.stop(details);
        details.classList.add('is-closing');

        details._fbAnim = p.panel.animate([{ height: from + 'px' }, { height: '0px' }], {
          duration: 220,
          easing: EASE_CLOSE,
          fill: 'forwards',
        });
        if (p.inner) {
          details._fbInnerAnim = p.inner.animate([{ opacity: 1 }, { opacity: 0 }], {
            duration: 160,
            easing: EASE_CLOSE,
            fill: 'forwards',
          });
        }
        var self = this;
        details._fbAnim.onfinish = function () {
          details.open = false;
          details.classList.remove('is-closing');
          self.stop(details);
        };
      }

      openInstant(details) {
        if (this.single) {
          var self = this;
          this.items().forEach(function (d) {
            if (d !== details && d.open) {
              self.stop(d);
              d.classList.remove('is-closing');
              d.open = false;
            }
          });
        }
        this.stop(details);
        details.classList.remove('is-closing');
        details.open = true;
      }

      onBlockSelect(event) {
        var target = event.target;
        if (!target || !this.contains(target)) return;
        var details = target.tagName === 'DETAILS' ? target : target.closest('details');
        if (details && details.closest('fb-accordion') === this) this.openInstant(details);
      }

      openFromHash() {
        var hash = window.location.hash;
        if (!hash || hash.length < 2) return;
        var target = null;
        try {
          target = document.getElementById(decodeURIComponent(hash.slice(1)));
        } catch (e) {
          return;
        }
        if (target && target.tagName === 'DETAILS' && target.closest('fb-accordion') === this) {
          this.openInstant(target);
        }
      }
    }
  );
})();
